/**
 * 钱包API服务层
 * 处理所有钱包相关的API请求
 */

import { apiRequest, API_BASE_URL } from '../config/api';
import { supabase } from '../config/supabase';
import { store } from '../store/store';

export interface WalletInfo {
  balance: number;
  total_recharged: number;
  total_spent: number;
}

export interface RechargeRequest {
  amount: number;
  payment_method: string;
}

export interface RechargeResponse {
  recharge_id: string;
  amount: number;
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  payment_url?: string;
}

export interface RechargeRecord {
  id: string;
  user_id: string;
  amount: number;
  payment_method: string;
  transaction_id?: string;
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  created_at: string;
  completed_at?: string;
}

export interface SpendingRecord {
  id: string;
  user_id: string;
  amount: number;
  item_type: string;
  item_id?: string;
  description: string;
  created_at: string;
}

export interface LearningStats {
  today: {
    date: string;
    conversations: number;
    messages: number;
    exercises: number;
    score: number;
    study_minutes: number;
  };
  this_week: {
    study_days: number;
    total_exercises: number;
    average_score: number;
    total_study_minutes: number;
  };
  this_month: {
    study_days: number;
    total_conversations: number;
    total_exercises: number;
    average_score: number;
    total_study_minutes: number;
  };
  overview: {
    current_streak: number;
    longest_streak: number;
    total_study_days: number;
    total_score: number;
    average_score: number;
    last_study_date?: string;
  };
}

/**
 * 获取用户认证token
 */
const getAuthToken = async (): Promise<string | null> => {
  try {
    // 首先尝试从Supabase获取token
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      console.log('Wallet API: Using Supabase JWT token');
      return session.access_token;
    }

    // 如果没有Supabase session，检查Redux中的用户信息
    const state = store.getState();
    const user = state.auth.user;
    
    if (user && user.id) {
      // 对于昵称登录的用户，使用用户ID作为token
      console.log('Wallet API: Using user ID as auth token for nickname user:', user.display_name);
      return user.id;
    }

    // 最后的fallback: 使用默认测试token
    console.log('Wallet API: Using fallback test token');
    return '550e8400-e29b-41d4-a716-446655440000';
  } catch (error) {
    console.error('Error getting auth token:', error);
    // Fallback: 使用默认测试token
    return '550e8400-e29b-41d4-a716-446655440000';
  }
};

/**
 * 获取认证headers
 */
const getAuthHeaders = async () => {
  const token = await getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * 钱包API服务类
 */
export class WalletApiService {
  /**
   * 获取用户钱包信息
   */
  static async getWalletInfo(): Promise<WalletInfo> {
    const headers = await getAuthHeaders();
    return apiRequest<WalletInfo>(`${API_BASE_URL}/wallet`, {
      method: 'GET',
      headers,
    });
  }

  /**
   * 发起充值请求
   */
  static async rechargeWallet(request: RechargeRequest): Promise<RechargeResponse> {
    const headers = await getAuthHeaders();
    return apiRequest<RechargeResponse>(`${API_BASE_URL}/wallet/recharge`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });
  }

  /**
   * 完成充值（模拟支付成功回调）
   */
  static async completeRecharge(rechargeId: string): Promise<void> {
    const headers = await getAuthHeaders();
    await apiRequest(`${API_BASE_URL}/wallet/recharge/${rechargeId}/complete`, {
      method: 'POST',
      headers,
    });
  }

  /**
   * 获取充值记录
   */
  static async getRechargeRecords(page: number = 1, limit: number = 20): Promise<RechargeRecord[]> {
    const headers = await getAuthHeaders();
    return apiRequest<RechargeRecord[]>(
      `${API_BASE_URL}/wallet/records/recharge?page=${page}&limit=${limit}`,
      {
        method: 'GET',
        headers,
      }
    );
  }

  /**
   * 获取消费记录
   */
  static async getSpendingRecords(page: number = 1, limit: number = 20): Promise<SpendingRecord[]> {
    const headers = await getAuthHeaders();
    return apiRequest<SpendingRecord[]>(
      `${API_BASE_URL}/wallet/records/spending?page=${page}&limit=${limit}`,
      {
        method: 'GET',
        headers,
      }
    );
  }

  /**
   * 获取学习统计数据
   */
  static async getLearningStats(): Promise<LearningStats> {
    const headers = await getAuthHeaders();
    return apiRequest<LearningStats>(`${API_BASE_URL}/stats/learning`, {
      method: 'GET',
      headers,
    });
  }

  /**
   * 更新学习统计（手动触发）
   */
  static async updateStats(date?: string): Promise<void> {
    const headers = await getAuthHeaders();
    const queryParams = date ? `?date=${date}` : '';
    await apiRequest(`${API_BASE_URL}/stats/update${queryParams}`, {
      method: 'POST',
      headers,
    });
  }

  /**
   * 格式化金额显示
   */
  static formatAmount(amount: number): string {
    return `¥${amount.toFixed(2)}`;
  }

  /**
   * 格式化日期显示
   */
  static formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * 获取充值状态的中文显示
   */
  static getStatusText(status: string): string {
    const statusMap: { [key: string]: string } = {
      pending: '待支付',
      completed: '已完成',
      failed: '失败',
      cancelled: '已取消',
    };
    return statusMap[status] || status;
  }

  /**
   * 获取消费类型的中文显示
   */
  static getItemTypeText(itemType: string): string {
    const typeMap: { [key: string]: string } = {
      ai_chat: 'AI对话',
      premium_feature: '高级功能',
      practice_session: '练习会话',
      translation: '翻译服务',
    };
    return typeMap[itemType] || itemType;
  }
}
